import { useNavigate } from 'react-router-dom'
import { track } from '../lib/analytics'
import type { SearchResult } from '../lib/types'

const TYPE_ICONS: Record<string, string> = {
  exchange: 'M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z',
  youtube: 'M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z',
  blog: 'M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 0 1-2.25 2.25M16.5 7.5V18a2.25 2.25 0 0 0 2.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 0 0 2.25 2.25h13.5',
  pdf: 'M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z',
  notecard: 'M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z',
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

export default function SearchResultCard({ result }: { result: SearchResult }) {
  const navigate = useNavigate()
  const iconPath = TYPE_ICONS[result.entry_type] || TYPE_ICONS.exchange

  return (
    <button
      onClick={() => {
        track('search_result_clicked', { thread_id: result.thread_id, entry_type: result.entry_type })
        navigate(`/research/${result.thread_id}?highlight=${result.entry_id}`)
      }}
      className="w-full text-left p-3 bg-surface rounded-lg border border-border hover:border-accent/50 transition-colors"
    >
      <div className="flex items-start gap-2">
        <svg
          className="w-4 h-4 mt-0.5 shrink-0 text-text-secondary"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
        </svg>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-medium text-text truncate">{result.thread_title}</h3>
            <span className="text-xs text-text-secondary shrink-0">
              {formatRelativeTime(result.created_at)}
            </span>
          </div>
          <p
            className="text-xs text-text-secondary mt-1 line-clamp-2 [&_mark]:bg-accent/30 [&_mark]:text-text [&_mark]:rounded-sm"
            dangerouslySetInnerHTML={{ __html: result.snippet }}
          />
          {!result.snippet.includes('<mark>') && (
            <span className="inline-block text-[10px] text-accent/70 mt-1 italic">Matched by topic</span>
          )}
          {result.tags.length > 0 && (
            <div className="flex gap-1 mt-1.5 overflow-x-auto">
              {result.tags.slice(0, 5).map((tag) => (
                <span
                  key={tag}
                  className="shrink-0 px-1.5 py-0.5 rounded text-[10px] bg-accent/10 text-accent"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </button>
  )
}
